import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { conditionNoteTypeService } from '../services/api';
import { ConditionNoteType } from '../types';

export interface ConditionNoteTypeState {
  types: ConditionNoteType[];
  loading: boolean;
  error: string | null;
}

const initialState: ConditionNoteTypeState = {
  types: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchConditionNoteTypes = createAsyncThunk(
  'conditionNoteTypes/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await conditionNoteTypeService.getTypes();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch condition note types');
    }
  }
);

export const createConditionNoteType = createAsyncThunk(
  'conditionNoteTypes/create',
  async (data: {
    name: string;
    name_en?: string;
    description?: string;
    icon?: string;
    color?: string;
    is_active?: boolean;
    sort_order?: number;
  }, { rejectWithValue }) => {
    try {
      const response = await conditionNoteTypeService.createType(data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create condition note type');
    }
  }
);

export const updateConditionNoteType = createAsyncThunk(
  'conditionNoteTypes/update',
  async ({ id, data }: {
    id: number;
    data: {
      name?: string;
      name_en?: string;
      description?: string;
      icon?: string;
      color?: string;
      is_active?: boolean;
      sort_order?: number;
    }
  }, { rejectWithValue }) => {
    try {
      const response = await conditionNoteTypeService.updateType(id, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update condition note type');
    }
  }
);

export const deleteConditionNoteType = createAsyncThunk(
  'conditionNoteTypes/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await conditionNoteTypeService.deleteType(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete condition note type');
    }
  }
);

const conditionNoteTypeSlice = createSlice({
  name: 'conditionNoteTypes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch types
    builder
      .addCase(fetchConditionNoteTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConditionNoteTypes.fulfilled, (state, action: PayloadAction<ConditionNoteType[]>) => {
        state.loading = false;
        state.types = action.payload;
      })
      .addCase(fetchConditionNoteTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create type
    builder
      .addCase(createConditionNoteType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConditionNoteType.fulfilled, (state, action: PayloadAction<ConditionNoteType>) => {
        state.loading = false;
        state.types.push(action.payload);
      })
      .addCase(createConditionNoteType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update type
    builder
      .addCase(updateConditionNoteType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConditionNoteType.fulfilled, (state, action: PayloadAction<ConditionNoteType>) => {
        state.loading = false;
        const index = state.types.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.types[index] = action.payload;
        }
      })
      .addCase(updateConditionNoteType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete type
    builder
      .addCase(deleteConditionNoteType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConditionNoteType.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.types = state.types.filter(t => t.id !== action.payload);
      })
      .addCase(deleteConditionNoteType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = conditionNoteTypeSlice.actions;
export default conditionNoteTypeSlice.reducer;

